import { postData, axiosPostData } from "./fetch_services";
import { getSurveySessionUrl } from "./streamSurveyChat";
import { withCsatToken } from "../../helpers/csatToken";

/**
 * Bootstrap a conversational survey via sf-api, then open a chat session on
 * sf-chat-service. Returns everything the Conversational page needs.
 *
 * @param {{ survey_id: string, location_id?: string|null }} args
 */
export async function bootstrapConversationalSurvey({ survey_id, location_id }) {
  const result = await postData("frontEmployeeFeedback/startConversationalSurvey", {
    survey_id,
    location_id: location_id || null,
  });

  if (!result?.success || !result?.data) {
    const err = new Error(result?.message || "Failed to start conversational survey");
    err.code = result?.code;
    throw err;
  }

  const { config, chatToken, chatServiceUrl } = result.data;

  const sessionRes = await fetch(getSurveySessionUrl(chatServiceUrl), {
    method: "POST",
    mode: "cors",
    headers: {
      Authorization: chatToken,
      "Content-Type": "application/json",
      Accept: "application/json",
    },
    body: JSON.stringify({ config }),
  });

  if (!sessionRes.ok) {
    throw new Error(`Failed to create survey chat session (HTTP ${sessionRes.status})`);
  }

  const sessionBody = await sessionRes.json();
  if (!sessionBody?.sessionId) {
    throw new Error("Chat service did not return a sessionId");
  }

  return {
    config,
    chatToken,
    chatServiceUrl,
    sessionId: sessionBody.sessionId,
  };
}

/**
 * Map survey Answer Type (questionType) → answer `type` used in jsonData.
 * Matches classic form components (RadioType, TextType, …) and ld-endpoints POC:
 *   radio / rating / emoji / dropdown / star_rating / multiselect / sequence / skills → "id"
 *   number → "number"
 *   text (and other free-form) → "text"
 * For "id" answers, `value` must be option _id (or array of option _ids).
 *
 * @param {string|undefined|null} questionType
 * @returns {"id"|"text"|"number"|"grid"|"inventory"}
 */
export function answerTypeFromQuestionType(questionType) {
  switch (questionType) {
    case "radio":
    case "rating":
    case "emoji":
    case "dropdown":
    case "star_rating":
    case "gridspot":
    case "multiselect":
    case "sequence":
    case "skills":
    case "employees":
      return "id";
    case "number":
      return "number";
    case "grid":
      return "grid";
    case "inventory":
      return "inventory";
    case "text":
    default:
      return "text";
  }
}

/**
 * Map Eva answers + conversation metadata into the existing answerSurvey FormData
 * contract so responses land in the standard survey dashboard.
 *
 * @param {object} args
 * @param {Record<string, { value: unknown, capture_method?: string, answer_text?: string }>} args.answers
 * @param {object} args.conversation
 * @param {object} [args.contactDetails]
 * @param {{ questions?: Array<{ id?: string, questionType?: string }> }|null} [args.config]
 *   Conversational config from startConversationalSurvey — used to set answer `type`
 *   from each question's Answer Type (e.g. Radio → type "id").
 */
export async function submitConversationalAnswers({
  answers,
  conversation,
  contactDetails = {},
  config = null,
}) {
  const questionTypeById = Object.fromEntries(
    (config?.questions || []).map((q) => [q.id, q.questionType])
  );

  const jsonData = {};
  Object.entries(answers || {}).forEach(([questionId, record]) => {
    if (!record) return;
    // Skip unanswered / null / empty values so scoring only sees real captures.
    if (
      record.value === null ||
      record.value === undefined ||
      record.value === "" ||
      (Array.isArray(record.value) && record.value.length === 0)
    ) {
      return;
    }
    const type = answerTypeFromQuestionType(questionTypeById[questionId]);
    jsonData[questionId] = {
      value: record.value,
      type,
      score_exclude: Boolean(record.score_exclude),
      capture_method: record.capture_method,
      ...(record.answer_text ? { note: record.answer_text } : {}),
      ...(record.write_in_value ? { write_in: record.write_in_value } : {}),
    };
  });

  // Prefer session config over localStorage — Login remounts / 401 handlers can
  // wipe storage mid-chat, which previously submitted empty ids → API 401 → reload → restart.
  const surveyId =
    config?.survey_id ||
    localStorage.getItem("survey") ||
    "";
  const companyId =
    config?.company_id ||
    localStorage.getItem("company_id") ||
    "";
  // Prefer session config (survives localStorage wipes mid-chat), same as survey/company.
  const locationId =
    config?.location_id ||
    localStorage.getItem("location") ||
    "";

  if (!surveyId) {
    throw new Error("Missing survey id — cannot submit conversational answers");
  }

  const metaData = {
    survey_id: surveyId,
  };
  if (locationId) {
    metaData.location_id = locationId;
  }

  const responseUserId = localStorage.getItem("user_id");
  if (responseUserId) {
    metaData.response_user_id = responseUserId;
  }

  const tagId = localStorage.getItem("tag_id") || localStorage.getItem("tag");
  if (tagId) {
    metaData.tag_id = tagId;
  }
  withCsatToken(metaData); // CSAT email link: which email this answers

  const responseData = {
    customer_name: contactDetails?.name || "",
    customer_email: contactDetails?.email || "",
    customer_phone: contactDetails?.phone || "",
  };

  const form_data = new FormData();
  // Match classic Questions submit — Login stores weblink / qrcode / web in localStorage.
  const source = localStorage.getItem("source") || "web";

  form_data.append("metaData", JSON.stringify(metaData));
  form_data.append("jsonData", JSON.stringify(jsonData));
  form_data.append("responseData", JSON.stringify(responseData));
  form_data.append("conversation", JSON.stringify(conversation || {}));
  form_data.append("source", source);
  if (companyId) {
    form_data.append("company_id", companyId);
  }

  // answerSurvey returns 401 for validation errors (e.g. missing survey id), not only
  // auth failures — never wipe the tab / reload on that path.
  return axiosPostData("survey/answerSurvey", form_data, {
    preserveSessionOn401: true,
  });
}
